<template>
	<div class="overlay-editor">
		<b-alert :show="!!error" variant="danger" dismissible class="m-2" @dismissed="error = null">{{error?.error||error}}</b-alert>
		<b-spinner v-if="loading" class="m-3" />
		<template v-else-if="overlay">
			<div class="oe-topbar">
				<b-button variant="link" class="text-body px-1" @click="$router.push('/overlays')" title="Back to overlays"><b-icon icon="arrow-left" /></b-button>
				<b-form-input
					v-if="editingTitle"
					v-model="titleDraft"
					size="sm"
					autofocus
					style="max-width: 260px"
					@keyup.enter="saveTitle"
					@keyup.esc="editingTitle = false"
					@blur="saveTitle"
				/>
				<template v-else>
					<h5 class="mb-0 ml-1">{{overlay.name}}</h5>
					<b-button variant="link" class="text-body px-1" @click="startEditTitle" title="Rename overlay"><b-icon icon="pencil" font-scale=".85" /></b-button>
				</template>
				<div class="flex-fill"></div>
				<b-button variant="primary" size="sm" class="mr-2" :id="addToObsBtnId"><b-icon icon="plus-circle-fill" class="mr-1" />Add to OBS</b-button>
				<b-popover :target="addToObsBtnId" triggers="click blur" placement="bottomleft" custom-class="overlay-url-popover">
					<b-form-group class="mb-0">
						<template #label>
							<div class="d-flex justify-content-between align-items-center">
								<span>OBS browser source URL</span>
								<a :href="outputUrl" target="_blank" rel="noopener noreferrer"><small>Open overlay <b-icon icon="box-arrow-up-right" /></small></a>
							</div>
						</template>
						<b-input-group>
							<b-form-input readonly :value="outputUrl" @focus="$event.target.select()" />
							<b-input-group-append><b-button variant="secondary" @click.capture.prevent.stop="copyOutputUrl"><b-icon icon="clipboard" /><span class="d-none d-sm-inline"> Copy</span></b-button></b-input-group-append>
						</b-input-group>
					</b-form-group>
				</b-popover>
				<b-button variant="outline-secondary" size="sm" @click="openSettings" title="Overlay settings"><b-icon icon="gear-fill" /></b-button>
			</div>

			<div class="oe-body">
				<div class="oe-scenes" :style="{ width: `${widths.scenes}px` }">
					<div class="oe-panel-header">
						<strong>Scenes</strong>
					</div>
					<draggable v-model="scenesModel" class="oe-scene-list" ghost-class="oe-ghost">
						<div
							v-for="scene in overlay.scenes"
							:key="scene.id"
							class="oe-scene"
							:class="{ active: scene.id === activeSceneId }"
							@click="selectScene(scene.id)"
							@dblclick="editScene(scene)"
							@contextmenu.prevent="sceneMenu($event, scene)"
						>
							<scene-thumbnail :overlay="overlay" :scene="scene" />
							<div class="oe-scene-name text-truncate">
								{{scene.name}}
								<small v-if="scene.obs_scene_name" class="text-muted" :title="`OBS scene: ${scene.obs_scene_name}`"><b-icon icon="camera-video" font-scale=".8" /> {{scene.obs_scene_name}}</small>
							</div>
						</div>
					</draggable>
					<div class="oe-scenes-footer">
						<b-button variant="primary" block class="oe-add-scene" @click="addScene"><b-icon icon="plus-lg" font-scale="1.4" /><span>Add scene</span></b-button>
					</div>
				</div>
				<div class="oe-resizer" @mousedown="startResize('scenes', $event)"></div>

				<div class="oe-layers" :style="{ width: `${widths.layers}px` }">
					<div class="oe-tabs">
						<a :class="{ active: tab === 'layers' }" @click="setTab('layers')">Layers</a>
						<a :class="{ active: tab === 'brand' }" @click="setTab('brand')">Brand</a>
					</div>
					<div class="oe-layers-body" :class="{ 'show-properties': showProperties }">
						<div class="oe-pane oe-pane-list">
							<div v-if="tab === 'layers'" class="oe-layer-actions">
								<b-button variant="secondary" size="sm" title="Undo (Ctrl+Z)" :disabled="!canUndo" @click="undo"><b-icon icon="arrow-counterclockwise" /></b-button>
								<b-button variant="secondary" size="sm" title="Redo (Ctrl+Shift+Z)" :disabled="!canRedo" @click="redo"><b-icon icon="arrow-clockwise" /></b-button>
								<b-button variant="secondary" size="sm" title="Paste layer (Ctrl+V)" :disabled="!activeScene || !clipboard" @click="pasteLayer()"><b-icon icon="clipboard-check" /></b-button>
								<b-button variant="primary" size="sm" class="ml-auto" :disabled="!activeScene" @click="showAddLayer = true"><b-icon icon="plus-lg" class="mr-1" />Add layer</b-button>
							</div>
						<draggable v-if="tab === 'layers' && activeScene" v-model="layersModel" class="oe-layer-list" ghost-class="oe-ghost" handle=".oe-layer-grip">
							<div
								v-for="layer in layersModel"
								:key="layer.id"
								class="oe-layer"
								:class="{ active: layer.id === selectedId, hidden: !layer.visible }"
								:style="layer.layer_color ? { background: `${layer.layer_color}99` } : null"
								@click="selectedId = layer.id"
								@dblclick="openProperties(layer.id, $event)"
								@contextmenu.prevent="layerMenu($event, layer)"
							>
								<div class="oe-layer-icon">
									<b-icon :icon="layerIcon(layer)" class="oe-layer-type" />
									<b-icon icon="grip-vertical" class="oe-layer-grip" />
								</div>
								<b-form-input
									v-if="renamingId === layer.id"
									v-model="layer.name"
									size="sm"
									autofocus
									@click.stop
									@dblclick.stop
									@keyup.enter="finishRename(layer)"
									@keyup.esc="finishRename(layer)"
									@blur="finishRename(layer)"
								/>
								<span v-else class="flex-fill text-truncate">{{layer.name}}</span>
								<b-icon :icon="layer.visible ? 'eye' : 'eye-slash'" class="oe-layer-toggle" :class="{ set: !layer.visible }" title="Toggle visibility" @click.stop="toggleVisible(layer)" />
								<b-icon :icon="layer.locked ? 'lock-fill' : 'unlock'" class="oe-layer-toggle" :class="{ set: layer.locked }" title="Toggle lock" @click.stop="toggleLocked(layer)" />
								<b-icon icon="three-dots-vertical" class="oe-layer-more" title="More" @click.stop="layerMoreMenu($event, layer)" />
							</div>
						</draggable>
						<div v-else-if="tab === 'brand'" class="p-2">
							<b-form-group label="Primary color" label-size="sm">
								<b-form-input type="color" :value="overlay.primary_color || '#7c5cff'" @change="patchOverlay({ primaryColor: $event })" />
							</b-form-group>
							<b-form-group label="Background color" label-size="sm">
								<b-form-input type="color" :value="overlay.background_color || '#000000'" @change="patchOverlay({ backgroundColor: $event })" />
							</b-form-group>
							<p class="small text-muted mb-0">Color fields on text, color and widget layers can follow these instead of a fixed color.</p>
						</div>
						</div>
						<div class="oe-pane oe-pane-properties">
							<template v-if="propertiesLayer">
								<div class="oe-properties-header">
									<b-button variant="link" size="sm" class="p-0 mr-2 text-body" title="Back to layers" @click="showProperties = false"><b-icon icon="arrow-left" /></b-button>
									<strong class="text-truncate">{{propertiesLayer.name}}</strong>
								</div>
								<div class="oe-properties">
									<layer-properties-form :layer="propertiesLayer" :overlay="overlay" :other-layers="propertiesOtherLayers" @save="saveLayer(propertiesLayer)" @source="applySource(propertiesLayer)" />
								</div>
							</template>
						</div>
					</div>
				</div>
				<div class="oe-resizer" @mousedown="startResize('layers', $event)"></div>

				<div class="oe-editor">
					<div class="oe-scene-toolbar">
						<b-button v-for="a in sceneActions" :key="a.label" size="sm" variant="secondary" :title="a.label" @click="runSceneAction(a)"><b-icon :icon="a.icon" class="mr-1" />{{a.label}}</b-button>
					</div>
					<div class="oe-stage-wrap">
						<overlay-stage
							:overlay="overlay"
							:scene="activeScene"
							:selected-id="selectedId"
							:warp-id="warpId"
							:crop-id="cropId"
							:stretch-id="stretchId"
							:preview-shared="previewShared"
							@select="selectedId = $event"
							@geometry="applyGeometry"
							@text="applyText"
							@warp="applyWarp"
							@crop="applyCrop"
							@properties="openProperties"
							@context="canvasMenu"
						>
							<template #toolbar="{ layer }">
								<layer-toolbar
									:layer="layer"
									:warping="layer.id === warpId"
									:cropping="layer.id === cropId"
									:stretching="layer.id === stretchId"
									:can-reset="canResetTransform(layer)"
									@distort="toggleWarp(layer)"
									@crop="toggleCrop(layer)"
									@stretch="toggleStretch(layer)"
									@reset-transform="resetTransform(layer)"
									@properties="openProperties(layer.id, $event)"
									@position="openPosition(layer.id, $event)"
									@move-up="moveLayer(layer, 1)"
									@move-down="moveLayer(layer, -1)"
									@duplicate="duplicateLayer(layer)"
									@delete="deleteLayer(layer)"
								/>
							</template>
						</overlay-stage>
					</div>
				</div>
			</div>
		</template>

		<floating-panel v-if="positionPanel && selectedLayer" title="Position and size" :x="positionPanel.x" :y="positionPanel.y" :width="240" @close="positionPanel = null">
			<layer-position-form :layer="selectedLayer" :overlay="overlay" :media-sizes="mediaSizes" @save="saveLayer(selectedLayer)" />
		</floating-panel>

		<b-modal v-model="showAddLayer" title="Add layer" size="lg" hide-footer>
			<h6 class="text-muted">Basic</h6>
			<div class="oe-add-grid mb-3">
				<b-button variant="secondary" @click="addLayerFromModal('image')"><b-icon icon="image" class="mr-2" />Image</b-button>
				<b-button variant="secondary" @click="addLayerFromModal('video')"><b-icon icon="camera-video" class="mr-2" />Video</b-button>
				<b-button variant="secondary" @click="addLayerFromModal('text')"><b-icon icon="fonts" class="mr-2" />Text</b-button>
				<b-button variant="secondary" @click="addLayerFromModal('color')"><b-icon icon="palette-fill" class="mr-2" />Color</b-button>
			</div>
			<h6 class="text-muted">Widgets</h6>
			<div class="oe-add-grid">
				<b-button v-for="wt in widgetTypes" :key="wt.value" variant="secondary" @click="addLayerFromModal('widget', { widgetType: wt.value })"><b-icon icon="grid-1x2-fill" class="mr-2" />{{wt.label}}</b-button>
			</div>
		</b-modal>

		<b-modal v-model="showSettings" title="Overlay settings" @ok="saveSettings">
			<div class="row">
				<div class="col-6"><b-form-group label="Width"><b-form-input type="number" v-model.number="settingsDraft.width" /></b-form-group></div>
				<div class="col-6"><b-form-group label="Height"><b-form-input type="number" v-model.number="settingsDraft.height" /></b-form-group></div>
			</div>
			<b-form-group label="OBS WebSocket URL"><b-form-input v-model="settingsDraft.obsWsUrl" placeholder="ws://localhost:4455" /></b-form-group>
			<b-form-group label="OBS WebSocket password"><b-form-input type="password" v-model="settingsDraft.obsWsPassword" /></b-form-group>
			<b-button size="sm" variant="secondary" :disabled="obsBusy || !settingsDraft.obsWsUrl" @click="testObs"><b-spinner v-if="obsBusy" small class="mr-1" />Test connection</b-button>
			<span v-if="obsMessage" class="ml-2 small" :class="obsScenes.length ? 'text-success' : 'text-danger'">{{obsMessage}}</span>
		</b-modal>

		<b-modal v-model="showSceneEdit" title="Edit scene" @ok="saveSceneEdit" @shown="loadObsScenes">
			<b-form-group label="Name"><b-form-input v-model="sceneDraft.name" @keyup.enter="saveSceneEdit" /></b-form-group>
			<b-form-group label="OBS scene" description="When OBS switches to this scene, the overlay switches to this scene too.">
				<b-form-input v-model="sceneDraft.obsSceneName" list="oe-obs-scenes" placeholder="Exact OBS scene name" />
				<b-form-datalist id="oe-obs-scenes" :options="obsScenes" />
				<small v-if="obsBusy" class="text-muted">Asking OBS for its scenes&hellip;</small>
				<small v-else-if="obsMessage && !obsScenes.length" class="text-muted">{{obsMessage}}</small>
			</b-form-group>
		</b-modal>

		<context-menu ref="ctx" />
	</div>
</template>

<script>
import draggable from 'vuedraggable';
import ContextMenu from '@/components/Overlay/ContextMenu.vue';
import FloatingPanel from '@/components/Overlay/FloatingPanel.vue';
import LayerToolbar from '@/components/Overlay/LayerToolbar.vue';
import LayerPositionForm from '@/components/Overlay/LayerPositionForm.vue';
import LayerPropertiesForm from '@/components/Overlay/LayerPropertiesForm.vue';
import OverlayStage from '@/components/Overlay/OverlayStage.vue';
import layerHistoryMixin from '@/components/Overlay/layerHistoryMixin';
import SceneThumbnail from '@/components/Overlay/SceneThumbnail.vue';
import ObsWebSocketClient from '@/ObsWebSocketClient';
import { renderSceneThumbnail } from '@/components/Overlay/renderSceneThumbnail';
import { WIDGET_TYPES, LAYER_COLORS, DEFAULT_PROPS, DEFAULT_SIZES, WIDGET_DEFAULT_SIZES, SCENE_TOOLBAR_ACTIONS, isMediaLayer } from '@/components/Overlay/layerTypes';
import { isChatWidgetType, createChatLayerSettings } from '@/overlay/chatSettings';
import { createSharedServices } from '@/overlay/widgetHost';

const WIDTHS_KEY = 'overlay-editor-widths';
const CLIPBOARD_KEY = 'overlay-editor-clipboard-layer';
const MIN_COLUMN_WIDTHS = { scenes: 140, layers: 200 };
const THUMBNAIL_INTERVAL_MS = 5000;

export default {
	mixins: [ layerHistoryMixin ],
	components: { draggable, ContextMenu, FloatingPanel, LayerToolbar, LayerPositionForm, LayerPropertiesForm, OverlayStage, SceneThumbnail },
	data() { return {
		error: null,
		loading: false,
		overlay: null,
		previewShared: null,
		activeSceneId: null,
		selectedId: null,
		warpId: null,
		cropId: null,
		stretchId: null,
		mediaSizes: {},
		tab: 'layers',
		editingTitle: false,
		titleDraft: '',
		renamingId: null,
		showSettings: false,
		settingsDraft: {},
		showSceneEdit: false,
		sceneDraft: {},
		positionPanel: null,
		clipboard: null,
		showProperties: false,
		propertiesLayerId: null,
		showAddLayer: false,
		obsScenes: [],
		obsBusy: false,
		obsMessage: '',
		widths: this.loadWidths(),
		widgetTypes: WIDGET_TYPES,
		addToObsBtnId: `add-to-obs-${Math.random().toString(36).slice(2)}`,
	}},
	computed: {
		activeScene() { return this.overlay?.scenes.find(s => s.id === this.activeSceneId) || null; },
		selectedLayer() { return this.activeScene?.layers.find(l => l.id === this.selectedId) || null; },
		// Kept after the panel is closed so its content stays put while it slides away.
		propertiesLayer() { return this.activeScene?.layers.find(l => l.id === this.propertiesLayerId) || null; },
		propertiesOtherLayers() { return (this.activeScene?.layers || []).filter(l => l.id !== this.propertiesLayerId); },
		otherLayers() { return (this.activeScene?.layers || []).filter(l => l.id !== this.selectedId); },
		outputUrl() { return this.overlay ? `${this.$baseUrl}/overlay.html?id=${encodeURIComponent(this.overlay.id)}` : ''; },
		scenesModel: {
			get() { return this.overlay.scenes; },
			set(list) { this.reorderScenes(list); },
		},
		layersModel: {
			get() { return [ ...this.activeScene.layers ].sort((a, b) => b.sort_order - a.sort_order); },
			set(topFirst) { this.reorderLayers([ ...topFirst ].reverse()); },
		},
		sceneActions() {
			const seen = new Set();
			const actions = [];
			for(const layer of this.activeScene?.layers || []) {
				for(const a of SCENE_TOOLBAR_ACTIONS[layer.type === 'widget' ? layer.props.widgetType : ''] || []) {
					if(!seen.has(a.label)) {
						seen.add(a.label);
						actions.push(a);
					}
				}
			}
			return actions;
		},
	},
	watch: {
		selectedId(id) {
			if(this.warpId !== null && id !== this.warpId) this.warpId = null;
			if(this.cropId !== null && id !== this.cropId) this.cropId = null;
			if(this.stretchId !== null && id !== this.stretchId) this.stretchId = null;
		},
		// So the toolbar knows whether an image or video layer differs from its natural size.
		'selectedLayer.props.src': {
			immediate: true,
			handler(src) {
				if(src && isMediaLayer(this.selectedLayer)) this.loadMediaSize(src, this.selectedLayer.type === 'video');
			},
		},
		activeSceneId(id, previousId) {
			const previous = this.overlay?.scenes.find(s => s.id === previousId);
			if(previous) this.refreshThumbnail(previous);
			this.selectedId = null;
			this.showProperties = false;
			if(this.activeScene) this.ensureHistory(this.activeScene);
		},
		selectedLayer(layer) {
			if(!layer) {
				this.positionPanel = null;
				this.showProperties = false;
			} else if(this.showProperties) {
				this.propertiesLayerId = layer.id;
			}
		},
	},
	created() {
		this._thumbSigs = {};
		this._mediaSizePromises = {};
		try { this.clipboard = JSON.parse(localStorage.getItem(CLIPBOARD_KEY)); } catch(e) { /* ignore */ }
	},
	async mounted() {
		window.addEventListener('keydown', this.onKeyDown);
		await this.loadOverlay();
	},
	beforeDestroy() {
		clearInterval(this.thumbTimer);
		if(this.activeScene) this.refreshThumbnail(this.activeScene);
		window.removeEventListener('keydown', this.onKeyDown);
	},
	methods: {
		loadWidths() {
			let widths = { scenes: 200, layers: 240 };
			try { widths = { ...widths, ...JSON.parse(localStorage.getItem(WIDTHS_KEY)) }; } catch(e) { /* defaults */ }
			for(const [ which, min ] of Object.entries(MIN_COLUMN_WIDTHS)) widths[which] = Math.max(min, widths[which]);
			return widths;
		},
		setTab(tab) {
			this.tab = tab;
			this.showProperties = false;
		},
		startResize(which, e) {
			const startX = e.clientX;
			const startWidth = this.widths[which];
			const onMove = ev => { this.widths[which] = Math.max(MIN_COLUMN_WIDTHS[which], Math.min(520, startWidth + ev.clientX - startX)); };
			const onUp = () => {
				document.removeEventListener('mousemove', onMove);
				document.removeEventListener('mouseup', onUp);
				document.body.style.userSelect = '';
				try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(this.widths)); } catch(err) { /* ignore */ }
			};
			document.body.style.userSelect = 'none';
			document.addEventListener('mousemove', onMove);
			document.addEventListener('mouseup', onUp);
		},
		async loadOverlay() {
			this.loading = true;
			try {
				this.overlay = await this.$api.get(`overlays/${this.$route.params.id}`);
				this.activeSceneId = this.overlay.scenes[0]?.id || null;
				// One socket/services instance for every widget layer preview in this editing session, whichever scene
				// they're in — same pattern the OBS output page uses, so previews behave exactly like the real thing.
				this.previewShared = createSharedServices({ hash: this.overlay.widgetHash });
				this.startThumbnails();
			} catch(e) {
				this.error = e;
			} finally {
				this.loading = false;
			}
		},

		// ── Scene thumbnails ──
		// Each scene is rendered into a small image that's saved with it, so the scene list shows those instead of
		// re-drawing every layer. Scenes are re-rendered every few seconds, but only when something about them changed.
		thumbnailSignature(scene) {
			return JSON.stringify([ this.snapshotLayers(scene), this.overlay.width, this.overlay.height, this.overlay.primary_color, this.overlay.background_color ]);
		},
		startThumbnails() {
			// A stored thumbnail is trusted as current until the scene is edited; only scenes without one are drawn now.
			for(const scene of this.overlay.scenes) {
				if(scene.thumbnail) this._thumbSigs[scene.id] = this.thumbnailSignature(scene);
			}
			this.refreshThumbnails();
			this.thumbTimer = setInterval(this.refreshThumbnails, THUMBNAIL_INTERVAL_MS);
		},
		async refreshThumbnails() {
			for(const scene of this.overlay?.scenes || []) await this.refreshThumbnail(scene);
		},
		async refreshThumbnail(scene) {
			const sig = this.thumbnailSignature(scene);
			if(this._thumbSigs[scene.id] === sig) return;
			this._thumbSigs[scene.id] = sig;
			try {
				const image = await renderSceneThumbnail(this.overlay, scene);
				if(image === scene.thumbnail) return;
				this.$set(scene, 'thumbnail', image);
				await this.$api.post(`overlays/${this.overlay.id}/scenes/${scene.id}/thumbnail`, { image });
			} catch(e) {
				console.error('Could not update scene thumbnail', e);
			}
		},
		run(promise) {
			return promise.catch(e => { this.error = e; });
		},
		async confirmDelete(message) {
			return this.$bvModal.msgBoxConfirm(message, { okVariant: 'danger', okTitle: 'Delete', size: 'sm', centered: true });
		},
		copyOutputUrl() {
			this.$util.copyToClipboard(this.outputUrl);
			this.$bvToast.toast('Copied overlay URL to clipboard', { title: 'Success', variant: 'success', solid: true });
		},

		// ── Overlay ──
		startEditTitle() {
			this.titleDraft = this.overlay.name;
			this.editingTitle = true;
		},
		async saveTitle() {
			if(!this.editingTitle) return;
			this.editingTitle = false;
			if(!this.titleDraft || this.titleDraft === this.overlay.name) return;
			await this.run(this.patchOverlay({ name: this.titleDraft }));
		},
		openSettings() {
			this.obsMessage = '';
			this.obsScenes = [];
			this.settingsDraft = {
				width: this.overlay.width,
				height: this.overlay.height,
				obsWsUrl: this.overlay.obs_ws_url,
				obsWsPassword: this.overlay.obs_ws_password,
			};
			this.showSettings = true;
		},
		saveSettings() {
			return this.run(this.patchOverlay(this.settingsDraft));
		},
		async patchOverlay(partial) {
			const payload = {
				name: this.overlay.name,
				width: this.overlay.width,
				height: this.overlay.height,
				primaryColor: this.overlay.primary_color,
				backgroundColor: this.overlay.background_color,
				obsWsUrl: this.overlay.obs_ws_url,
				obsWsPassword: this.overlay.obs_ws_password,
				...partial,
			};
			await this.$api.patch(`overlays/${this.overlay.id}`, payload);
			Object.assign(this.overlay, {
				name: payload.name,
				width: payload.width,
				height: payload.height,
				primary_color: payload.primaryColor,
				background_color: payload.backgroundColor,
				obs_ws_url: payload.obsWsUrl,
				obs_ws_password: payload.obsWsPassword,
			});
		},

		// ── OBS ──
		async fetchObsScenes(url, password) {
			const client = new ObsWebSocketClient({ url, password }).connect();
			try {
				await client.whenConnected();
				return await client.getSceneNames();
			} finally {
				client.close();
			}
		},
		async loadObsScenes() {
			this.obsScenes = [];
			this.obsMessage = '';
			if(!this.overlay.obs_ws_url) {
				this.obsMessage = 'Set the OBS WebSocket URL in the overlay settings to pick from OBS\'s scenes.';
				return;
			}
			this.obsBusy = true;
			try {
				this.obsScenes = await this.fetchObsScenes(this.overlay.obs_ws_url, this.overlay.obs_ws_password);
			} catch(e) {
				this.obsMessage = `Could not reach OBS: ${e.message}`;
			} finally {
				this.obsBusy = false;
			}
		},
		async testObs() {
			this.obsScenes = [];
			this.obsMessage = '';
			this.obsBusy = true;
			try {
				this.obsScenes = await this.fetchObsScenes(this.settingsDraft.obsWsUrl, this.settingsDraft.obsWsPassword);
				this.obsMessage = `Connected — ${this.obsScenes.length} scene${this.obsScenes.length === 1 ? '' : 's'} found.`;
			} catch(e) {
				this.obsMessage = e.message;
			} finally {
				this.obsBusy = false;
			}
		},

		// ── Scenes ──
		selectScene(id) {
			this.activeSceneId = id;
		},
		async addScene() {
			const name = `Scene ${this.overlay.scenes.length + 1}`;
			const res = await this.run(this.$api.post(`overlays/${this.overlay.id}/scenes`, { name }));
			if(!res) return;
			this.overlay.scenes.push({ id: res.id, name, obs_scene_name: null, thumbnail: null, layers: [] });
			this.activeSceneId = res.id;
		},
		editScene(scene) {
			this.sceneDraft = { id: scene.id, name: scene.name, obsSceneName: scene.obs_scene_name || '' };
			this.showSceneEdit = true;
		},
		async saveSceneEdit() {
			const scene = this.overlay.scenes.find(s => s.id === this.sceneDraft.id);
			if(!scene || !this.sceneDraft.name) return;
			this.showSceneEdit = false;
			const ok = await this.run(this.$api.patch(`overlays/${this.overlay.id}/scenes/${scene.id}`, { name: this.sceneDraft.name, obsSceneName: this.sceneDraft.obsSceneName }));
			if(!ok) return;
			scene.name = this.sceneDraft.name;
			scene.obs_scene_name = this.sceneDraft.obsSceneName || null;
		},
		async duplicateScene(scene) {
			const res = await this.run(this.$api.post(`overlays/${this.overlay.id}/scenes/${scene.id}/duplicate`));
			if(!res) return;
			const fresh = await this.run(this.$api.get(`overlays/${this.overlay.id}`));
			const created = fresh?.scenes.find(s => s.id === res.id);
			if(!created) return;
			this.overlay.scenes.push(created);
			this.activeSceneId = created.id;
		},
		async deleteScene(scene) {
			if(!await this.confirmDelete(`Delete scene "${scene.name}"?`)) return;
			if(!await this.run(this.$api.delete(`overlays/${this.overlay.id}/scenes/${scene.id}`))) return;
			this.overlay.scenes = this.overlay.scenes.filter(s => s.id !== scene.id);
			if(this.activeSceneId === scene.id) this.activeSceneId = this.overlay.scenes[0]?.id || null;
		},
		async reorderScenes(list) {
			this.overlay.scenes = list;
			await this.run(this.$api.post(`overlays/${this.overlay.id}/scenes/reorder`, { orderedSceneIds: list.map(s => s.id) }));
		},
		sceneMenu(e, scene) {
			this.$refs.ctx.open(e, [
				{ label: 'Edit', icon: 'pencil', action: () => this.editScene(scene) },
				{ label: 'Duplicate', icon: 'files', action: () => this.duplicateScene(scene) },
				{ separator: true },
				{ label: 'Paste layer', icon: 'clipboard-check', disabled: !this.clipboard, action: () => this.pasteLayer(scene) },
				{ separator: true },
				{ label: 'Delete', icon: 'trash', danger: true, action: () => this.deleteScene(scene) },
			]);
		},

		// ── Layers ──
		layerIcon(layer) {
			return { image: 'image', video: 'camera-video', text: 'fonts', color: 'palette-fill', widget: 'grid-1x2-fill' }[layer.type] || 'square';
		},
		widgetLabel(value) {
			return this.widgetTypes.find(w => w.value === value)?.label || value;
		},
		addLayerFromModal(type, opts) {
			this.showAddLayer = false;
			return this.addLayer(type, opts);
		},
		async addLayer(type, opts = {}) {
			const scene = this.activeScene;
			if(!scene) return;
			const [ w, h ] = (type === 'widget' && WIDGET_DEFAULT_SIZES[opts.widgetType]) || DEFAULT_SIZES[type];
			const props = DEFAULT_PROPS[type](opts.widgetType);
			if(type === 'widget') {
				// Each widget layer is a standalone instance; seed it with the account's current settings for that widget
				// (both chat layer types start from the account's chat widget).
				const account = await this.$api.get(`widgetSettings/${isChatWidgetType(opts.widgetType) ? 'chat' : opts.widgetType}`).catch(() => ({})) || {};
				props.settings = isChatWidgetType(opts.widgetType) ? createChatLayerSettings(account) : account;
			}
			const point = opts.point;
			const data = {
				type,
				name: type === 'widget' ? this.widgetLabel(opts.widgetType) : type[0].toUpperCase() + type.slice(1),
				visible: true,
				locked: false,
				x: Math.round(point ? Math.max(0, Math.min(this.overlay.width - w, point[0] - w / 2)) : (this.overlay.width - w) / 2),
				y: Math.round(point ? Math.max(0, Math.min(this.overlay.height - h, point[1] - h / 2)) : (this.overlay.height - h) / 2),
				w,
				h,
				rotation: 0,
				props,
			};
			const res = await this.run(this.$api.post(`overlays/${this.overlay.id}/scenes/${scene.id}/layers`, data));
			if(!res) return;
			scene.layers.push({ id: res.id, scene_id: scene.id, layer_color: null, sort_order: Math.max(-1, ...scene.layers.map(l => l.sort_order)) + 1, ...data });
			this.selectedId = res.id;
			this.commitHistory();
			// An image or video layer is empty until it has a file, so go straight to choosing one.
			if(type === 'image' || type === 'video') this.pickLayerFile(res.id);
		},
		async pickLayerFile(id) {
			this.openProperties(id);
			await this.$nextTick();
			this.$el.querySelector('.oe-properties input[type=file]')?.click();
		},
		saveLayer(layer) {
			this.commitHistory();
			return this.run(this.$api.patch(`overlays/${this.overlay.id}/scenes/${this.activeScene.id}/layers/${layer.id}`, {
				name: layer.name,
				layerColor: layer.layer_color,
				visible: layer.visible,
				locked: layer.locked,
				x: layer.x,
				y: layer.y,
				w: layer.w,
				h: layer.h,
				rotation: layer.rotation,
				props: layer.props,
			}));
		},
		applyGeometry(id, geometry) {
			const layer = this.activeScene.layers.find(l => l.id === id);
			if(!layer) return;
			Object.assign(layer, geometry);
			this.saveLayer(layer);
		},
		toggleWarp(layer) {
			this.warpId = this.warpId === layer.id ? null : layer.id;
			if(this.warpId !== null) { this.cropId = null; this.stretchId = null; }
		},
		toggleCrop(layer) {
			this.cropId = this.cropId === layer.id ? null : layer.id;
			if(this.cropId !== null) { this.warpId = null; this.stretchId = null; }
		},
		toggleStretch(layer) {
			this.stretchId = this.stretchId === layer.id ? null : layer.id;
			if(this.stretchId !== null) { this.cropId = null; this.warpId = null; }
		},
		// The natural size of an image or video, or null when it can't be read.
		loadMediaSize(src, isVideo) {
			return this._mediaSizePromises[src] ||= new Promise(resolve => {
				const el = document.createElement(isVideo ? 'video' : 'img');
				const done = size => {
					if(size) this.$set(this.mediaSizes, src, size);
					else delete this._mediaSizePromises[src];
					resolve(size);
				};
				if(isVideo) {
					el.preload = 'metadata';
					el.onloadedmetadata = () => done(el.videoWidth ? [ el.videoWidth, el.videoHeight ] : null);
				} else {
					el.onload = () => done(el.naturalWidth ? [ el.naturalWidth, el.naturalHeight ] : null);
				}
				el.onerror = () => done(null);
				el.src = src;
			});
		},
		// A new image or video takes the shape of its file (shrunk to fit the overlay if it's bigger), around the same centre.
		async applySource(layer) {
			const size = layer.props.src ? await this.loadMediaSize(layer.props.src, layer.type === 'video') : null;
			if(size) {
				const k = Math.min(1, this.overlay.width / size[0], this.overlay.height / size[1]);
				const w = Math.max(1, Math.round(size[0] * k));
				const h = Math.max(1, Math.round(size[1] * k));
				const cx = layer.x + layer.w / 2;
				const cy = layer.y + layer.h / 2;
				layer.w = w;
				layer.h = h;
				layer.x = Math.round(cx - w / 2);
				layer.y = Math.round(cy - h / 2);
			}
			this.saveLayer(layer);
		},
		canResetTransform(layer) {
			if(layer.rotation || layer.props?.warp) return true;
			if(!isMediaLayer(layer) || !layer.props?.src) return false;
			const size = this.mediaSizes[layer.props.src];
			return !size || size[0] !== layer.w || size[1] !== layer.h;
		},
		// Rotation, perspective distortion and crop go back to none, and an image or video goes back to its own size (around the same centre).
		async resetTransform(layer) {
			this.warpId = null;
			this.cropId = null;
			layer.rotation = 0;
			this.$delete(layer.props, 'warp');
			this.$delete(layer.props, 'crop');
			const size = isMediaLayer(layer) && layer.props.src ? await this.loadMediaSize(layer.props.src, layer.type === 'video') : null;
			if(size) {
				const cx = layer.x + layer.w / 2;
				const cy = layer.y + layer.h / 2;
				layer.w = size[0];
				layer.h = size[1];
				layer.x = Math.round(cx - size[0] / 2);
				layer.y = Math.round(cy - size[1] / 2);
			}
			this.saveLayer(layer);
		},
		applyWarp(id, warp) {
			const layer = this.activeScene.layers.find(l => l.id === id);
			if(!layer) return;
			if(warp) this.$set(layer.props, 'warp', warp);
			else this.$delete(layer.props, 'warp');
			this.saveLayer(layer);
		},
		// A crop-handle drag: the box itself was trimmed (or, dragged back out, un-trimmed), so its geometry
		// changes right alongside the crop fraction.
		applyCrop(id, { x, y, w, h, crop }) {
			const layer = this.activeScene.layers.find(l => l.id === id);
			if(!layer) return;
			Object.assign(layer, { x, y, w, h });
			if(crop) this.$set(layer.props, 'crop', crop);
			else this.$delete(layer.props, 'crop');
			this.saveLayer(layer);
		},
		applyText(id, text) {
			const layer = this.activeScene.layers.find(l => l.id === id);
			if(!layer) return;
			layer.props.text = text;
			this.saveLayer(layer);
		},
		toggleVisible(layer) {
			layer.visible = !layer.visible;
			this.saveLayer(layer);
		},
		toggleLocked(layer) {
			layer.locked = !layer.locked;
			this.saveLayer(layer);
		},
		setLayerColor(layer, color) {
			layer.layer_color = color;
			this.saveLayer(layer);
		},
		startRename(layer) {
			this.selectedId = layer.id;
			this.renamingId = layer.id;
		},
		finishRename(layer) {
			if(this.renamingId !== layer.id) return;
			this.renamingId = null;
			if(!layer.name) layer.name = 'Layer';
			this.saveLayer(layer);
		},
		async duplicateLayer(layer) {
			const res = await this.run(this.$api.post(`overlays/${this.overlay.id}/scenes/${this.activeScene.id}/layers/${layer.id}/duplicate`));
			if(!res) return;
			const copy = JSON.parse(JSON.stringify(layer));
			this.activeScene.layers.push({ ...copy, id: res.id, name: `${layer.name} copy`, x: layer.x + 20, y: layer.y + 20, sort_order: Math.max(...this.activeScene.layers.map(l => l.sort_order)) + 1 });
			this.selectedId = res.id;
			this.commitHistory();
		},
		// Clipboard is persisted so it survives switching scenes (or overlays) between copy and paste.
		copyLayer(layer) {
			this.clipboard = {
				sourceSceneId: this.activeScene.id,
				type: layer.type,
				name: layer.name,
				layer_color: layer.layer_color,
				visible: layer.visible,
				locked: layer.locked,
				x: layer.x, y: layer.y, w: layer.w, h: layer.h,
				rotation: layer.rotation,
				props: JSON.parse(JSON.stringify(layer.props)),
			};
			try { localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(this.clipboard)); } catch(e) { /* ignore */ }
			this.$bvToast.toast(`Copied "${layer.name}"`, { title: 'Success', variant: 'success', solid: true });
		},
		// Pasting into the scene it was copied from offsets the copy so it doesn't land directly on top;
		// pasting into another scene keeps the original position, or drops it at a right-clicked point.
		async pasteLayer(targetScene = this.activeScene, point) {
			const c = this.clipboard;
			if(!c || !targetScene) return;
			const sameScene = c.sourceSceneId === targetScene.id;
			const x = point ? Math.round(point[0] - c.w / 2) : (sameScene ? c.x + 20 : c.x);
			const y = point ? Math.round(point[1] - c.h / 2) : (sameScene ? c.y + 20 : c.y);
			const props = JSON.parse(JSON.stringify(c.props));
			const res = await this.run(this.$api.post(`overlays/${this.overlay.id}/scenes/${targetScene.id}/layers`, {
				type: c.type, name: c.name, layerColor: c.layer_color, visible: c.visible, locked: c.locked,
				x, y, w: c.w, h: c.h, rotation: c.rotation, props,
			}));
			if(!res) return;
			targetScene.layers.push({
				id: res.id, scene_id: targetScene.id, type: c.type, name: c.name, layer_color: c.layer_color,
				visible: c.visible, locked: c.locked, x, y, w: c.w, h: c.h, rotation: c.rotation, props,
				sort_order: Math.max(-1, ...targetScene.layers.map(l => l.sort_order)) + 1,
			});
			if(targetScene.id === this.activeSceneId) this.selectedId = res.id;
			this.commitHistory(targetScene);
		},
		async deleteLayer(layer) {
			if(!await this.confirmDelete(`Delete layer "${layer.name}"?`)) return;
			if(!await this.run(this.$api.delete(`overlays/${this.overlay.id}/scenes/${this.activeScene.id}/layers/${layer.id}`))) return;
			this.activeScene.layers = this.activeScene.layers.filter(l => l.id !== layer.id);
			if(this.selectedId === layer.id) this.selectedId = null;
			this.commitHistory();
		},
		// direction: 1 = towards the front. Moves by swapping places with the neighbouring layer in that direction.
		moveLayer(layer, direction) {
			const asc = [ ...this.activeScene.layers ].sort((a, b) => a.sort_order - b.sort_order);
			const i = asc.findIndex(l => l.id === layer.id);
			const j = i + direction;
			if(j < 0 || j >= asc.length) return;
			[ asc[i], asc[j] ] = [ asc[j], asc[i] ];
			this.reorderLayers(asc);
		},
		moveLayerTo(layer, front) {
			const asc = [ ...this.activeScene.layers ].sort((a, b) => a.sort_order - b.sort_order).filter(l => l.id !== layer.id);
			if(front) asc.push(layer);
			else asc.unshift(layer);
			this.reorderLayers(asc);
		},
		async reorderLayers(ascending) {
			ascending.forEach((l, i) => { l.sort_order = i; });
			this.commitHistory();
			await this.run(this.$api.post(`overlays/${this.overlay.id}/scenes/${this.activeScene.id}/layers/reorder`, { orderedLayerIds: ascending.map(l => l.id) }));
		},

		// The path of a scene's layers, for the shared undo/redo (layerHistoryMixin).
		layersApiBase(scene) {
			return `overlays/${this.overlay.id}/scenes/${scene.id}/layers`;
		},

		// ── Panels & menus ──
		openProperties(id) {
			this.selectedId = id;
			this.tab = 'layers';
			this.propertiesLayerId = id;
			this.showProperties = true;
		},
		openPosition(id, e) {
			this.selectedId = id;
			const toolbar = e.currentTarget.closest('.overlay-layer-toolbar').getBoundingClientRect();
			this.positionPanel = { x: toolbar.right + 10, y: toolbar.top };
		},
		runSceneAction(action) {
			this.run(action.run(this.$api));
		},
		layerMenuItems(layer, e) {
			const asc = [ ...this.activeScene.layers ].sort((a, b) => a.sort_order - b.sort_order);
			const index = asc.findIndex(l => l.id === layer.id);
			return [
				{ label: 'Properties', icon: 'gear', action: () => this.openProperties(layer.id, e) },
				{ label: 'Position and size', icon: 'bounding-box', action: () => { this.selectedId = layer.id; this.positionPanel = { x: e.clientX, y: e.clientY }; } },
				isMediaLayer(layer) ? { label: 'Crop', icon: 'crop', action: () => { this.selectedId = layer.id; this.toggleCrop(layer); } } : null,
				{ label: 'Reset transform', icon: 'bootstrap-reboot', disabled: !this.canResetTransform(layer), action: () => this.resetTransform(layer) },
				{ label: 'Rename', icon: 'pencil', action: () => this.startRename(layer) },
				{ swatches: LAYER_COLORS, value: layer.layer_color, onPick: c => this.setLayerColor(layer, c) },
				{ separator: true },
				{ label: layer.visible ? 'Hide' : 'Show', icon: layer.visible ? 'eye-slash' : 'eye', action: () => this.toggleVisible(layer) },
				{ label: layer.locked ? 'Unlock' : 'Lock', icon: layer.locked ? 'unlock' : 'lock', action: () => this.toggleLocked(layer) },
				{ separator: true },
				{ label: 'Bring to front', icon: 'chevron-double-up', disabled: index === asc.length - 1, action: () => this.moveLayerTo(layer, true) },
				{ label: 'Move up', icon: 'arrow-up', disabled: index === asc.length - 1, action: () => this.moveLayer(layer, 1) },
				{ label: 'Move down', icon: 'arrow-down', disabled: index === 0, action: () => this.moveLayer(layer, -1) },
				{ label: 'Send to back', icon: 'chevron-double-down', disabled: index === 0, action: () => this.moveLayerTo(layer, false) },
				{ separator: true },
				{ label: 'Duplicate', icon: 'files', action: () => this.duplicateLayer(layer) },
				{ label: 'Copy', icon: 'clipboard', action: () => this.copyLayer(layer) },
				{ label: 'Delete', icon: 'trash', danger: true, action: () => this.deleteLayer(layer) },
			];
		},
		layerMoreMenu(e, layer) {
			const rect = e.currentTarget.getBoundingClientRect();
			const at = { clientX: rect.left, clientY: rect.bottom + 4, preventDefault() {} };
			this.selectedId = layer.id;
			this.$refs.ctx.open(at, this.layerMenuItems(layer, at));
		},
		layerMenu(e, layer) {
			this.selectedId = layer.id;
			this.$refs.ctx.open(e, this.layerMenuItems(layer, e));
		},
		canvasMenu(e, { layerId, hits, point }) {
			const layer = layerId && this.activeScene.layers.find(l => l.id === layerId);
			const items = [];
			if(layer) {
				if(hits.length > 1) {
					items.push({ label: 'Select layer', icon: 'layers', children: hits.map(h => ({ label: h.name, icon: this.layerIcon(h), action: () => { this.selectedId = h.id; } })) });
					items.push({ separator: true });
				}
				items.push(...this.layerMenuItems(layer, e));
			}
			if(this.activeScene) {
				items.push({ label: 'Paste', icon: 'clipboard-check', disabled: !this.clipboard, action: () => this.pasteLayer(this.activeScene, point) });
				if(layer) items.push({ separator: true });
				items.push({
					label: 'Add layer',
					icon: 'plus-lg',
					children: [
						{ label: 'Image', icon: 'image', action: () => this.addLayer('image', { point }) },
						{ label: 'Video', icon: 'camera-video', action: () => this.addLayer('video', { point }) },
						{ label: 'Text', icon: 'fonts', action: () => this.addLayer('text', { point }) },
						{ label: 'Color', icon: 'palette-fill', action: () => this.addLayer('color', { point }) },
					],
				});
				items.push({
					label: 'Add widget',
					icon: 'grid-1x2-fill',
					children: this.widgetTypes.map(wt => ({ label: wt.label, action: () => this.addLayer('widget', { widgetType: wt.value, point }) })),
				});
			}
			this.$refs.ctx.open(e, items);
		},
		onKeyDown(e) {
			if(/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable) return;
			if(document.querySelector('.modal.show')) return;
			const layer = this.selectedLayer;
			if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); if(e.shiftKey) this.redo(); else this.undo(); return; }
			if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); this.redo(); return; }
			if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') { if(layer) { e.preventDefault(); this.copyLayer(layer); } return; }
			if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') { if(this.clipboard && this.activeScene) { e.preventDefault(); this.pasteLayer(); } return; }
			if(e.key === 'Escape') { this.selectedId = null; return; }
			if(!layer) return;
			if(e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); this.deleteLayer(layer); return; }
			const step = e.shiftKey ? 10 : 1;
			const delta = { ArrowLeft: [ -step, 0 ], ArrowRight: [ step, 0 ], ArrowUp: [ 0, -step ], ArrowDown: [ 0, step ] }[e.key];
			if(!delta || layer.locked) return;
			e.preventDefault();
			layer.x += delta[0];
			layer.y += delta[1];
			clearTimeout(this.nudgeTimer);
			this.nudgeTimer = setTimeout(() => this.saveLayer(layer), 300);
		},
	},
};
</script>

<!-- The popover is rendered outside this component, so its styles can't be scoped. -->
<style lang="scss">
@import '../plugins/variable.scss';

.overlay-url-popover {
	max-width: 560px;
	width: 560px;

	a {
		color: darken($vaporwave-green, 45%);

		&:hover { color: darken($vaporwave-green, 35%); }
	}
}
</style>

<style lang="scss" scoped>
@import '../plugins/variable.scss';

.overlay-editor {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

.oe-topbar {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	padding: .4rem .75rem;
	border-bottom: 1px solid $border-color;
	gap: .25rem;
}

.oe-body {
	flex: 1 1 auto;
	display: flex;
	min-height: 0;
}

.oe-scenes,
.oe-layers {
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
	min-height: 0;
	background: $gray-200;
}

.oe-layers {
	min-width: 200px;
}

.oe-resizer {
	flex: 0 0 5px;
	cursor: col-resize;
	background: $border-color;

	&:hover { background: $green; }
}

.oe-panel-header,
.oe-tabs {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: .4rem .6rem;
	border-bottom: 1px solid $border-color;
}

.oe-tabs {
	gap: 1rem;
	padding-bottom: 0;

	a {
		cursor: pointer;
		padding-bottom: .4rem;
		border-bottom: 2px solid transparent;
		color: $text-muted;
		text-decoration: none;

		&.active { color: #fff; border-bottom-color: $green; }
	}
}

.oe-scene-list,
.oe-layer-list {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}

.oe-scene-list { padding: .5rem; }

// Icon over a small label, so it never wraps in a narrow column.
.oe-add-scene {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: .25rem;
	padding: .9rem .5rem;
	border-radius: .6rem;
	font-size: .8rem;
	font-weight: 600;
	line-height: 1.1;
	white-space: nowrap;
}

// Outside the scrolling list so it stays in view however many scenes there are.
.oe-scenes-footer {
	flex: 0 0 auto;
	padding: .6rem;
	border-top: 1px solid $border-color;
}

.oe-scene {
	margin-bottom: .6rem;
	padding: 4px;
	border: 2px solid transparent;
	border-radius: .35rem;
	cursor: pointer;

	&:hover { background: $gray-400; }
	&.active { border-color: $green; background: $gray-400; }
}

.oe-scene-name {
	padding-top: .2rem;
	font-size: .8rem;
}

.oe-layer-list { padding: .5rem; }

.oe-layer-actions {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	gap: .35rem;
	padding: .5rem;
	border-bottom: 1px solid $border-color;
}

.oe-add-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
	gap: .5rem;

	.btn { text-align: left; }
}

.oe-layers-body {
	position: relative;
	flex: 1 1 auto;
	min-height: 0;
	overflow: hidden;
}

// The list and the properties sit side by side and slide past each other.
.oe-pane {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	background: $gray-200;
	transition: transform .25s ease, opacity .25s ease;
}

.oe-pane-list { transform: translateX(0); }
.oe-pane-properties { transform: translateX(100%); opacity: 0; visibility: hidden; transition: transform .25s ease, opacity .25s ease, visibility 0s .25s; }

.show-properties {
	.oe-pane-list { transform: translateX(-100%); opacity: 0; }
	.oe-pane-properties { transform: translateX(0); opacity: 1; visibility: visible; transition: transform .25s ease, opacity .25s ease, visibility 0s; }
}

.oe-properties-header {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	padding: .5rem .6rem;
	border-bottom: 1px solid $border-color;
}

.oe-properties {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	padding: .6rem;
}

.oe-layer {
	display: flex;
	align-items: center;
	margin-bottom: .35rem;
	padding: .5rem .6rem;
	border-radius: .4rem;
	background: rgba(255, 255, 255, .05);
	cursor: pointer;

	&:hover { background-color: $gray-400; }
	&.active { box-shadow: inset 0 0 0 2px $green; }
	&.hidden { opacity: .55; }
}

.oe-layer-icon {
	position: relative;
	flex: 0 0 auto;
	width: 18px;
	height: 18px;
	margin-right: .55rem;

	.oe-layer-type,
	.oe-layer-grip {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		transition: opacity .1s;
	}

	.oe-layer-grip { opacity: 0; cursor: grab; }
}

// On hover the drag handle takes the place of the layer's type icon.
.oe-layer:hover .oe-layer-icon {
	.oe-layer-type { opacity: 0; }
	.oe-layer-grip { opacity: .85; }
}

// Toggles and the menu only show on hover, except a hidden or locked layer keeps showing its state.
.oe-layer-toggle,
.oe-layer-more {
	flex: 0 0 auto;
	margin-left: .5rem;
	cursor: pointer;
	visibility: hidden;
	opacity: .8;

	&:hover { opacity: 1; }
}

.oe-layer-toggle.set { visibility: visible; }
.oe-layer:hover .oe-layer-toggle,
.oe-layer:hover .oe-layer-more { visibility: visible; }

.oe-ghost { opacity: .4; }

.oe-editor {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
}

.oe-scene-toolbar {
	flex: 0 0 auto;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: .35rem;
	min-height: 2.75rem;
	padding: .4rem .6rem;
}

.oe-stage-wrap {
	flex: 1 1 auto;
	min-height: 0;
}
</style>
